import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import api from "../../api";
import ApiClient from "../../api";
import {
  LearningMaterialTag,
  LearningMaterialTagList,
  LearningMaterialTagType,
  LearningMaterialTagWithDeletable,
} from "../../domains/LearningMaterialTag";
import { createError, HTTPErrors } from "../../errors";
import { getNextPageParam } from "../../helpers/ReactQueryHelper";
import { useFlashMessage } from "../../hooks/useFlashMessage";
import ApiResponse from "../../interfaces/ApiResponse";
import SectionInterface from "../../interfaces/SectionInterface";

export type NarrowingConditions = LearningMaterialTagType | "all";

const makeNarrowingConditionParam = (condition: NarrowingConditions) => {
  switch (condition) {
    case "default":
      return ["default"];
    case "custom":
      return ["custom"];
    case "all":
    default:
      return ["custom", "default"];
  }
};

type Props = {
  section: SectionInterface;
  narrowingCondition: NarrowingConditions;
  shouldGetAllRecords?: boolean;
};

export const useFetchLearningMaterialTags = (props: Props) => {
  const path = baseUrl(props.section.id);

  const result = useInfiniteQuery<
    ApiResponse<ReadonlyArray<LearningMaterialTag>>,
    HTTPErrors
  >({
    queryKey: cacheKeyOf(props.section.id, props.narrowingCondition),
    queryFn: async ({ pageParam }) => {
      const res = await api.interruptGet(`/api/v1${path}`, {
        query: {
          tag_types: makeNarrowingConditionParam(props.narrowingCondition),
          page: pageParam,
          no_pagination: props.shouldGetAllRecords,
        },
      });
      if (res.ok) {
        const json = await res.json();
        return json.learningMaterialTags;
      }
      throw await createError(res);
    },
    initialPageParam: 1,
    getNextPageParam,
  });

  const data = result.data?.pages.flatMap((page) => page.data);
  return { ...result, data };
};

type UseAssociateLearningMaterialTagProps = {
  sectionId: string;
  onSuccess?: () => void;
  onError?: () => void;
};

type AssociateLearningMaterialTagRequestParam = {
  learningMaterialCode: string;
  learningMaterialTags: LearningMaterialTagList;
};

export const useAssociateLearningMaterialTag = ({
  sectionId,
  onSuccess,
  onError,
}: UseAssociateLearningMaterialTagProps) => {
  const { showSuccessMessage, showErrorMessage } = useFlashMessage();
  const {
    mutate,
    isPending: isLoading,
    isError,
  } = useMutation<void, Error, AssociateLearningMaterialTagRequestParam>({
    mutationFn: async ({ learningMaterialCode, learningMaterialTags }) => {
      const response = await ApiClient.put(
        `/api/v1/sections/${sectionId}/management_learning_materials/${learningMaterialCode}/tags`,
        {
          learning_material_tag_ids: learningMaterialTags.map((tag) => tag.id),
        },
      );

      if (response.ok) {
        showSuccessMessage("教材タグを設定しました");
      } else {
        showErrorMessage("教材タグを設定できませんでした");
      }
    },
    onSuccess,
    onError,
  });
  return {
    mutate,
    isLoading,
    isError,
  };
};

export const useLearningMaterialTagsCache = (
  sectionId: string,
  narrowingCondition: NarrowingConditions = "all",
) => {
  const client = useQueryClient();
  const result = client.getQueryData<
    InfiniteData<ApiResponse<ReadonlyArray<LearningMaterialTagWithDeletable>>>
  >(cacheKeyOf(sectionId, narrowingCondition));
  return result?.pages.flatMap((page) => page.data);
};

export const cacheKeyOf = (
  sectionId: string,
  narrowingCondition: NarrowingConditions = "all",
) => [baseUrl(sectionId), narrowingCondition];

const baseUrl = (sectionId: string) =>
  `/sections/${sectionId}/learning_material_tags`;
